import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const ChowaThemeContext = createContext();

export const ChowaThemeProvider = ({ children }) => {
  // chowa独立のダーク/ライトテーマ管理
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('chowa_isDark');
    return savedTheme ? JSON.parse(savedTheme) : true;
  });
  
  const [selectedSeriesColor, setSelectedSeriesColor] = useState(null);

  // TMS接続状態管理を追加
  const [tmsConnectionState, setTmsConnectionState] = useState({
    isConnected: false,
    currentModelId: null,
    wsStatus: '未接続',
    countdown: 0
  });

  // ダークモードの切り替え
  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  // ダークモード設定をローカルストレージに保存
  useEffect(() => {
    localStorage.setItem('chowa_isDark', JSON.stringify(isDark));
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // シリーズカラー定義
  const seriesColorConfig = {
    HV: {
      primary: '#3b82f6', // 青
      light: '#dbeafe',
      dark: '#1e40af',
      name: 'blue'
    },
    SR: {
      primary: '#f97316', // オレンジ
      light: '#fed7aa',
      dark: '#ea580c',
      name: 'orange'
    },
    ZV: {
      primary: '#9333ea', // 紫
      light: '#e9d5ff',
      dark: '#7c3aed',
      name: 'purple'
    }
  };

  // デフォルトテーマ（グレー系）- isDarkの状態に応じて動的に取得
  const getDefaultTheme = () => ({
    primary: isDark ? '#6b7280' : '#4b5563', // グレー
    light: isDark ? '#374151' : '#f3f4f6',
    dark: isDark ? '#111827' : '#1f2937',
    name: 'gray'
  });

  // 現在適用されているテーマカラーを取得
  const getCurrentTheme = () => {
    if (selectedSeriesColor && seriesColorConfig[selectedSeriesColor]) {
      return seriesColorConfig[selectedSeriesColor];
    }
    return getDefaultTheme();
  };

  // シリーズ選択時のカラー設定
  const setSeriesColor = (categoryKey) => {
    setSelectedSeriesColor(categoryKey);
  };

  // シリーズ選択をクリア（デフォルトに戻す）
  const clearSeriesColor = () => {
    setSelectedSeriesColor(null);
  };

  // TMS接続状態を更新する関数
  const updateTmsConnectionState = useCallback((newState) => {
    setTmsConnectionState(prev => ({ ...prev, ...newState }));
  }, []);

  // TMS接続状態をリセットする関数
  const resetTmsConnectionState = useCallback(() => {
    setTmsConnectionState({
      isConnected: false,
      currentModelId: null,
      wsStatus: '未接続',
      countdown: 0
    });
  }, []);

  // CSS変数を動的に更新してサイドバーなどの色を変更
  useEffect(() => {
    const currentTheme = getCurrentTheme();
    const root = document.documentElement;
    
    // chowa専用のCSS変数を設定
    root.style.setProperty('--chowa-primary-color', currentTheme.primary);
    root.style.setProperty('--chowa-light-color', currentTheme.light);
    root.style.setProperty('--chowa-dark-color', currentTheme.dark);
    
    // サイドバーの色を直接変更（chowa内のみ適用）
    const chowaContainer = document.querySelector('[data-chowa-app]');
    if (chowaContainer) {
      // サイドバーナビゲーションの色を変更
      const sidebarElements = chowaContainer.querySelectorAll('[data-sidebar-element]');
      sidebarElements.forEach(element => {
        if (selectedSeriesColor) {
          element.style.setProperty('--tw-bg-opacity', '1');
          element.style.backgroundColor = `rgb(${hexToRgb(currentTheme.primary)} / var(--tw-bg-opacity))`;
        } else {
          element.style.backgroundColor = '';
        }
      });
    }
  }, [selectedSeriesColor, isDark]);

  // 16進数カラーをRGBに変換するヘルパー関数
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
      `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}` : 
      '107 114 128'; // fallback gray
  };

  const value = {
    // 全体テーマ
    isDark,
    toggleTheme,
    
    // chowa専用テーマ
    selectedSeriesColor,
    setSeriesColor,
    clearSeriesColor,
    getCurrentTheme,
    seriesColorConfig,
    
    // 便利なヘルパー
    isSeriesSelected: !!selectedSeriesColor,
    currentThemeName: getCurrentTheme()?.name || 'gray',
    
    // TMS接続状態管理を追加
    tmsConnectionState,
    updateTmsConnectionState,
    resetTmsConnectionState
  };

  return (
    <ChowaThemeContext.Provider value={value}>
      {children}
    </ChowaThemeContext.Provider>
  );
};

export const useChowaTheme = () => {
  const context = useContext(ChowaThemeContext);
  if (context === undefined) {
    throw new Error('useChowaTheme must be used within a ChowaThemeProvider');
  }
  return context;
};

export default ChowaThemeContext; 